/**
 * Observabilidad — salud de gateways LoRaWAN, baterías, ingestión, pipelines.
 */
import { useQuery } from "@tanstack/react-query";

import { api } from "../api/client";
import { runtime } from "../api/runtime";
import { InsightCard } from "../components/InsightCard";
import { PageHeader } from "../layout/PageHeader";
import { ensureDemoProviders } from "../lib/demoRuntime";
import { loadInsightsBundle } from "../lib/insightsRuntime";
import {
  getObservabilidad,
  type EstadoMetricaIA,
  type ReporteAIObservability,
} from "../lib/observability";
import type { Ventana } from "../api/contracts";

interface Observability {
  gateways: Array<{
    gateway_id: string;
    dma_codigo: string;
    estado: "online" | "degradado" | "offline";
    rssi_dbm: number;
    snr_db: number;
    uptime_pct: number;
    paquetes_perdidos_pct: number;
    firmware: string;
    medidores: number;
  }>;
  baterias: { criticas: number; bajas: number; ok: number };
  ingestion: {
    lecturas_hora: number;
    lag_segundos: number;
    backlog: number;
    tasa_error_pct: number;
    pipelines: Array<{ nombre: string; estado: string; p95_ms: number }>;
  };
}

function estadoTone(e: string): "accent" | "warn" | "danger" {
  if (e === "online" || e === "healthy") return "accent";
  if (e === "degradado" || e === "degraded") return "warn";
  return "danger";
}

/** Etiquetas legibles de las seis métricas de AI_Observability (R14.3). */
const ETIQUETAS_METRICA_IA: Record<EstadoMetricaIA["nombre"], string> = {
  alucinacion: "Tasa de alucinación",
  retrieval_quality: "Calidad de recuperación",
  operator_trust: "Confianza del operador",
  acceptance_rate: "Tasa de aceptación IA",
  false_positive: "Tasa de falsos positivos",
  reversion: "Tasa de reversión",
};

/** Métricas derivadas de los enlaces rec·dec·outcome de la Memoria (R14.5, R20.4). */
const METRICAS_DERIVADAS_MEMORIA = new Set<EstadoMetricaIA["nombre"]>([
  "acceptance_rate",
  "false_positive",
  "reversion",
]);

/**
 * Computa el {@link ReporteAIObservability} sobre una ventana de 24h
 * usando la Observabilidad demo (singleton compartido con `runtime.obs()`),
 * que deriva acceptance_rate/false_positive/reversión de los enlaces
 * rec·dec·outcome de la Memoria_Operacional vía `runtime.memoria()`.
 */
async function loadAIObservability(): Promise<ReporteAIObservability> {
  ensureDemoProviders();
  // Garantiza que la Memoria demo quede registrada para derivar métricas.
  runtime.memoria();
  const hasta = new Date();
  const desde = new Date(hasta.getTime() - 24 * 60 * 60 * 1000);
  const ventana: Ventana = { desde: desde.toISOString(), hasta: hasta.toISOString() };
  return getObservabilidad().reporteAI(ventana);
}

export function Observability(): JSX.Element {
  const q = useQuery<Observability>({
    queryKey: ["observability"],
    queryFn: () => api<Observability>("/observability"),
    staleTime: 30_000,
  });
  const o = q.data;

  // Motor_Insights: Insights de infraestructura/operación correlacionados
  // sobre el Grafo_Entidades, consumidos vía el runtime selector (R5.7).
  const motorQuery = useQuery({
    queryKey: ["motor-insights"],
    queryFn: loadInsightsBundle,
    staleTime: 60_000,
  });
  const motorInsights = motorQuery.data?.insights ?? [];

  // AI_Observability — métricas de calidad de la IA derivadas de la
  // Memoria_Operacional (acceptance_rate, false_positive, reversión) y de
  // las muestras registradas (alucinación, retrieval_quality, operator_trust),
  // consumidas vía el runtime selector (R14.3, R14.5, R20.4, R20.5).
  const aiObsQuery = useQuery({
    queryKey: ["ai-observability"],
    queryFn: loadAIObservability,
    staleTime: 30_000,
  });
  const aiReporte = aiObsQuery.data;

  return (
    <>
      <PageHeader
        eyebrow="Plataforma"
        title="Observabilidad IoT"
        subtitle="Salud de la red LoRaWAN, baterías de medidores, ingestión y pipelines."
      />
      {o && (
        <>
          <div className="card-grid" style={{ marginBottom: 16 }}>
            <div className="kpi kpi--accent">
              <span className="kpi__label">Ingestión</span>
              <span className="kpi__value">{(o.ingestion.lecturas_hora / 1000).toFixed(0)}k/h</span>
              <span className="kpi__hint">lag {o.ingestion.lag_segundos}s</span>
            </div>
            <div className="kpi kpi--info">
              <span className="kpi__label">Backlog</span>
              <span className="kpi__value">{o.ingestion.backlog.toLocaleString("es-CL")}</span>
              <span className="kpi__hint">tasa error {o.ingestion.tasa_error_pct}%</span>
            </div>
            <div className="kpi kpi--danger">
              <span className="kpi__label">Baterías críticas</span>
              <span className="kpi__value">{o.baterias.criticas}</span>
              <span className="kpi__hint">{o.baterias.bajas} bajas · {o.baterias.ok.toLocaleString("es-CL")} ok</span>
            </div>
            <div className="kpi kpi--warn">
              <span className="kpi__label">Gateways offline</span>
              <span className="kpi__value">
                {o.gateways.filter((g) => g.estado === "offline").length}
              </span>
              <span className="kpi__hint">{o.gateways.length} totales</span>
            </div>
          </div>

          <section className="card" style={{ marginBottom: 12 }}>
            <div className="card__title">Gateways LoRaWAN</div>
            <div className="table-wrapper" style={{ borderColor: "transparent" }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Gateway</th>
                    <th>DMA</th>
                    <th>Estado</th>
                    <th className="numeric">RSSI</th>
                    <th className="numeric">SNR</th>
                    <th className="numeric">Uptime</th>
                    <th className="numeric">Pérdida pkt</th>
                    <th className="numeric">Medidores</th>
                    <th>Firmware</th>
                  </tr>
                </thead>
                <tbody>
                  {o.gateways.map((g) => (
                    <tr key={g.gateway_id}>
                      <td className="text-mono">{g.gateway_id}</td>
                      <td>{g.dma_codigo}</td>
                      <td>
                        <span className={`badge badge--${estadoTone(g.estado)}`}>{g.estado}</span>
                      </td>
                      <td className="numeric">{g.rssi_dbm} dBm</td>
                      <td className="numeric">{g.snr_db} dB</td>
                      <td className="numeric">{g.uptime_pct}%</td>
                      <td className="numeric">{g.paquetes_perdidos_pct}%</td>
                      <td className="numeric">{g.medidores.toLocaleString("es-CL")}</td>
                      <td className="text-mono">{g.firmware}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="card">
            <div className="card__title">Pipelines de datos</div>
            <div className="table-wrapper" style={{ borderColor: "transparent" }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Pipeline</th>
                    <th>Estado</th>
                    <th className="numeric">p95</th>
                  </tr>
                </thead>
                <tbody>
                  {o.ingestion.pipelines.map((p) => (
                    <tr key={p.nombre}>
                      <td className="text-mono">{p.nombre}</td>
                      <td>
                        <span className={`badge badge--${estadoTone(p.estado)}`}>{p.estado}</span>
                      </td>
                      <td className="numeric">{p.p95_ms} ms</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {/* AI_Observability — métricas de calidad de la IA (R14.3, R14.5, R20.4, R20.5) */}
      <section className="card" style={{ marginTop: 12 }}>
        <div className="card__title">
          <span>AI_Observability · calidad de la IA</span>
          {aiReporte?.incidente_revision_humana && (
            <span className="badge badge--danger">Revisión humana</span>
          )}
        </div>
        <p className="text-muted" style={{ fontSize: 12, marginBottom: 12 }}>
          Seis métricas en rango [0, 1] sobre una ventana de 24 h. Aceptación, falsos positivos y
          reversión se derivan de los enlaces recomendación·decisión·outcome de la Memoria_Operacional.
          Una métrica sin datos se marca <em>no disponible</em> (distinta de 0).
        </p>
        {aiObsQuery.isLoading && (
          <div className="status-block status-block--info">Calculando métricas de IA…</div>
        )}
        {aiReporte?.memoria_degradada && (
          <div className="status-block status-block--warn" style={{ marginBottom: 12 }}>
            Memoria_Operacional no disponible: aceptación, falsos positivos y reversión usan muestras
            registradas como respaldo.
          </div>
        )}
        {aiReporte && (
          <div className="card-grid">
            {aiReporte.metricas.map((m) => {
              const tono = m.excede ? (m.revision_humana ? "danger" : "warn") : "accent";
              return (
                <div
                  key={m.nombre}
                  className={`kpi kpi--${tono}`}
                  style={m.excede ? { outline: "2px solid var(--c-danger, #c0392b)" } : undefined}
                >
                  <span className="kpi__label">{ETIQUETAS_METRICA_IA[m.nombre]}</span>
                  <span className="kpi__value">
                    {m.valor.disponible ? m.valor.valor.toFixed(2) : "N/D"}
                  </span>
                  <span className="kpi__hint">
                    {m.valor.disponible
                      ? `umbral ${m.umbral.toFixed(2)}${m.excede ? " · supera" : ""}`
                      : "dato no disponible"}
                    {METRICAS_DERIVADAS_MEMORIA.has(m.nombre) ? " · Memoria" : ""}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Motor_Insights — Insights operacionales correlacionados (R5.7) */}
      <section className="card" style={{ marginTop: 12, background: "var(--c-surface-2)" }}>
        <div className="card__title">
          <span>Insights operacionales · Motor_Insights</span>
          <span className="text-muted text-mono">{motorInsights.length}</span>
        </div>
        <p className="text-muted" style={{ fontSize: 12, marginBottom: 12 }}>
          Correlaciones cross-domain sobre el Grafo_Entidades con causalidad, evidencia y confianza.
        </p>
        {motorQuery.isLoading && (
          <div className="status-block status-block--info">Correlacionando señales…</div>
        )}
        {!motorQuery.isLoading && motorInsights.length === 0 && (
          <div className="status-block">Sin Insights fundamentados disponibles.</div>
        )}
        <div className="vstack">
          {motorInsights.map((ins) => (
            <InsightCard key={ins.insight_id} insight={ins} compact />
          ))}
        </div>
      </section>
    </>
  );
}
